import type { ReactElement } from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { PixelPlaceholderLoader, LegendBullet } from "@similarweb/ui-components";

import type { IUser } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import { LoaderSuggestedWrapper } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardStyled";
import type { IStepMetadata } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { ButtonsList } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/ButtonsList";
import { Step } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/Step";
import { useSimilarSites } from "app/services/sitesService/hooks/useSimilarSites";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const BENCHMARK_COMPETITORS_COLORS: readonly string[] = [
    colorsPalettes.orange["400"],
    colorsPalettes.mint["400"],
    colorsPalettes.solutions.SALES,
];

const SuggestedList = styled(FlexRow)`
    gap: 12px;
`;

const SuggestedTitle = styled.div`
    margin-bottom: 12px;
    ${setFontVariant("subtitle2", colorsPalettes.carbon[250])};
`;

const LegendWrapper = styled(FlexRow)`
    height: 28px;
    background: ${colorsPalettes.carbon[0]};
    border: 1px solid ${colorsPalettes.carbon[100]};
    box-shadow: 0 3px 6px rgba(14, 30, 62, 0.08);
    border-radius: 4px;
    box-sizing: border-box;
    align-items: center;
    padding: 4px 0 4px 8px;
    gap: 8px;
`;

export const TrafficAndEngagementStep: React.FC<IStepMetadata> = (props) => {
    const i18n = i18nFilter();
    const userWebsiteQuiz = (swSettings.user as IUser)?.customData?.quiz?.website;
    const { data: similarSitesData } = useSimilarSites(
        userWebsiteQuiz || "",
        { limit: 3 },
        {
            enabled: !!userWebsiteQuiz,
        },
    );

    const suggestedWebsites = useMemo(() => {
        if (similarSitesData?.length > 0) {
            return similarSitesData.map((similarSite) => similarSite.Domain);
        }
        return ["amazon.com", "ebay.com", "aliexpress.com"];
    }, [similarSitesData]);

    const userWebsite = userWebsiteQuiz || "";

    const compareMultipleWebsites = (): void => {
        const websites = userWebsite ? [userWebsite, ...suggestedWebsites] : suggestedWebsites;

        props.onStepComplete("growth.homepage.wizard.button.trafficAndEngagementSuggested");

        proNavigator.go("digitalsuite_website_trafficandengagement", {
            key: [websites.toString()],
            webSource: "Total",
            duration: "3m",
            isWWW: "*",
            country: 999,
        });
    };

    const viewSingleWebsite = (): void => {
        props.onStepComplete("growth.homepage.wizard.button.trafficAndEngagementTryOwn");

        proNavigator.go(
            "digitalsuite_website_trafficandengagement",
            proNavigator.getState("digitalsuite_website_trafficandengagement").defaultQueryParams,
        );
    };

    const renderSuggestedWebsites = (): ReactElement => {
        return (
            <FlexColumn>
                <SuggestedTitle>
                    <I18n>growth.homepage.activationWizard.suggested</I18n>
                </SuggestedTitle>
                <SuggestedList>
                    {suggestedWebsites.map((website, index) => {
                        const suggestedWebsite = {
                            color: BENCHMARK_COMPETITORS_COLORS[index],
                            text: website,
                        };

                        return (
                            <LegendWrapper key={suggestedWebsite.color}>
                                <LegendBullet
                                    text={suggestedWebsite.text}
                                    size={0}
                                    labelColor={suggestedWebsite.color}
                                />
                            </LegendWrapper>
                        );
                    })}
                </SuggestedList>
            </FlexColumn>
        );
    };

    return (
        <Step
            {...props}
            title={i18n("growth.homepage.activationWizard.step.traffic_and_engagement.title")}
            description={i18n("growth.homepage.activationWizard.step.traffic_and_engagement.description")}
            additions={
                !(suggestedWebsites.length === 0) ? (
                    renderSuggestedWebsites()
                ) : (
                    <LoaderSuggestedWrapper>
                        <PixelPlaceholderLoader
                            width={100}
                            height={16}
                        />
                        <PixelPlaceholderLoader
                            width={360}
                            height={28}
                        />
                    </LoaderSuggestedWrapper>
                )
            }
            buttonsList={
                <ButtonsList
                    buttons={[
                        {
                            text: i18n("growth.homepage.activationWizard.step.traffic_and_engagement.button.1"),
                            onClick: compareMultipleWebsites,
                            type: "primary",
                        },
                        {
                            text: i18n("growth.homepage.activationWizard.step.traffic_and_engagement.button.2"),
                            onClick: viewSingleWebsite,
                            type: "flat",
                        },
                    ]}
                />
            }
        />
    );
};
